import { ZButton } from "@/shared/components/button";
import { Avatar, AvatarGroup, Card, CardBody, Tooltip } from "@nextui-org/react";
import { useCallback, useState } from "react";
import RiskLevelChip from "../../VendorRiskAssessment/Overview/RiskLevelChip";

interface SourceReference {
  url: string;
  domain: string;
  indices: number[];
  status: "success" | "warning" | "info";
}

interface BreachItemProps {
  vendor: string;
  icon: "github" | "google" | "microsoft" | "openai";
  description: string;
  risk: "Low" | "Medium" | "High";
  date: string;
  sources?: SourceReference[];
}

export function BreachItem({ vendor, icon, description, risk, date, sources }: BreachItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [tooltipStates, setTooltipStates] = useState<{ [key: string]: boolean }>({});

  // Count the number of lines (approximation based on characters)
  const shouldTruncate = description.split("\n").length > 3 || description.length > 300;
  const truncatedDescription = shouldTruncate && !isExpanded ? description.slice(0, 300) + "..." : description;

  const handleOpenChange = useCallback((key: string, open: boolean) => {
    setTooltipStates(prev => ({ ...prev, [key]: open }));
  }, []);

  const handleViewClick = useCallback((url: string, key: string) => {
    handleOpenChange(key, false);
    window.open(url, '_blank');
  }, [handleOpenChange]);

  const getIcon = () => {
    switch (icon) {
      case "github":
        return (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="text-gray-700">
            <path
              fillRule="evenodd"
              d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"
            />
          </svg>
        );
      case "google":
        return (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M15.68 8.18182C15.68 7.61455 15.6291 7.06909 15.5345 6.54545H8V9.64364H12.3055C12.12 10.64 11.5564 11.4836 10.7091 12.0509V14.0618H13.2945C14.8073 12.6691 15.68 10.6182 15.68 8.18182Z"
              fill="#4285F4"
            />
            <path
              d="M8 16C10.16 16 11.9709 15.2873 13.2945 14.0618L10.7091 12.0509C9.99273 12.5345 9.07636 12.8218 8 12.8218C5.91636 12.8218 4.15273 11.4182 3.52 9.52H0.850909V11.5927C2.16727 14.2036 4.87273 16 8 16Z"
              fill="#34A853"
            />
            <path
              d="M3.52 9.52C3.36 9.03636 3.27273 8.52545 3.27273 8C3.27273 7.47455 3.36 6.96364 3.52 6.48V4.40727H0.850909C0.309091 5.48727 0 6.70909 0 8C0 9.29091 0.309091 10.5127 0.850909 11.5927L3.52 9.52Z"
              fill="#FBBC05"
            />
            <path
              d="M8 3.17818C9.17818 3.17818 10.2255 3.58545 11.0618 4.37818L13.3527 2.08727C11.9673 0.792727 10.1564 0 8 0C4.87273 0 2.16727 1.79636 0.850909 4.40727L3.52 6.48C4.15273 4.58182 5.91636 3.17818 8 3.17818Z"
              fill="#EA4335"
            />
          </svg>
        );
      case "microsoft":
        return (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect width="7.2" height="7.2" fill="#F25022" />
            <rect x="8.8" width="7.2" height="7.2" fill="#7FBA00" />
            <rect y="8.8" width="7.2" height="7.2" fill="#00A4EF" />
            <rect x="8.8" y="8.8" width="7.2" height="7.2" fill="#FFB900" />
          </svg>
        );
      case "openai":
        return <div className="w-4 h-4 bg-gray-700 rounded-full flex items-center justify-center text-white text-xs">O</div>;
      default:
        return null;
    }
  };

  return (
    <Card className="shadow-none border border-gray-200 rounded-lg">
      <CardBody className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {getIcon()}
          <span className="font-medium text-sm">{vendor}</span>
          <RiskLevelChip riskLevel={risk} />
        </div>
        <p className="text-xs text-gray-500 mb-2">{date}</p>
        <p className="text-sm text-gray-600 leading-relaxed">
          {truncatedDescription}
          {shouldTruncate && !isExpanded && (
            <button onClick={() => setIsExpanded(true)} className="text-blue-600 hover:text-blue-700 ml-1">
              more
            </button>
          )}
        </p>
        {sources && sources.length > 0 && (
          <div className="flex items-center gap-2 mt-3">
            <AvatarGroup
              isBordered
              classNames={{
                count:
                  "flex relative justify-center items-center box-border overflow-hidden align-middle z-0 outline-none data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2 text-primary-foreground rounded-full ring-0 bg-[#006FEE] bg-opacity-20 text-primary ring-offset-2 ring-offset-background dark:ring-offset-background-dark -ms-0 data-[hover=true]:-translate-x-1 rtl:data-[hover=true]:translate-x-1 transition-transform data-[focus-visible=true]:-translate-x-1 rtl:data-[focus-visible=true]:translate-x-1 ring-primary w-4 h-4 text-tiny",
              }}
              color="primary"
            >
              {sources.map((source, index) => {
                const tooltipKey = `${index}_${source.indices[0]}`;
                return (
                  <Tooltip
                    showArrow={true}
                    placement="top"
                    shouldFlip={true}
                    key={tooltipKey}
                    isDismissable={true}
                    closeDelay={100}
                    isOpen={tooltipStates[tooltipKey]}
                    onOpenChange={(open) => handleOpenChange(tooltipKey, open)}
                    classNames={{
                      content: "bg-black/95 backdrop-blur-md text-white text-tiny p-3",
                      arrow: "backdrop-blur-md",
                      base: "before:bg-black/95",
                    }}
                    content={
                      <div className="flex flex-col gap-y-[0.5rem] max-w-96">
                        <div className="max-h-[5rem] overflow-hidden">
                          <p className="line-clamp-4 text-white break-all">{source.url}</p>
                        </div>
                        <div className="flex gap-3">
                          <p className="text-tiny text-[#A5A5A5] break-all">{source.domain}</p>
                        </div>
                        <div className="self-end flex gap-2">
                          <ZButton
                            size="sm"
                            type="button"
                            onClick={() => handleViewClick(source.url, tooltipKey)}
                          >
                            View Reference
                          </ZButton>
                        </div>
                      </div>
                    }
                  >
                    <Avatar
                      key={tooltipKey}
                      classNames={{
                        icon: "[&>span]:text-primary",
                        base: `
                          flex relative justify-center items-center box-border overflow-hidden align-middle z-0 outline-none
                          data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus
                          data-[focus-visible=true]:outline-offset-2 text-primary-foreground rounded-full ring-0 bg-[#006FEE]
                          bg-opacity-20 text-primary ring-offset-2 ring-offset-background dark:ring-offset-background-dark -ms-0
                          data-[hover=true]:-translate-x-1 rtl:data-[hover=true]:translate-x-1 transition-transform
                          data-[focus-visible=true]:-translate-x-1 rtl:data-[focus-visible=true]:translate-x-1 ring-primary
                          w-4 h-4 text-[10px]
                        `,
                      }}
                      icon={<span>{source.indices[0]}</span>}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenChange(tooltipKey, !tooltipStates[tooltipKey]);
                      }}
                    >
                      {source.indices[0]}
                    </Avatar>
                  </Tooltip>
                );
              })}
            </AvatarGroup>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
